import { Grid } from "@mui/material";
import { BottomActions } from "app/components/BottomActions";
import DataTable from "app/components/DataTable";
import { CustomDrawer } from "app/components/Drawer";
import { NoPermission } from "app/components/NoPermission";
import { ScrollableView } from "app/components/ScrollableView";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectFeaturePermissions } from "store/global/authPermission/selector";
import { PermissionFeature } from "store/global/authPermission/types";
import { getFormattedDateTime } from "utils/helpers";
import useWindowDimensions from "utils/hooks/useWindowDimensions";
import { useInjectReducer, useInjectSaga } from "utils/redux-injectors";
import Topbar from "../Dashboard/components/topbar";
import FiltersForm from "./components/FiltersForm";
import DailyActivityAddModal from "./components/Form";
import { UploadedFileCell } from "app/components/UploadedFileCell";
import { useUserSaga } from "./redux/saga";
import { selectList, selectLoading, selectQuery } from "./redux/selector";
import { actions, reducer, sliceKey } from "./redux/slice";
import { useStyles } from "./styles";
import { Item } from "./types";

const DailyActivity = () => {
  const listitems = useSelector(selectList);
  const { meta, items } = listitems;

  const { q, orderField, orderDirection, organizationId } =
    useSelector(selectQuery);

  const featurePermissions = useSelector(selectFeaturePermissions);
  const { hasFullAccess, hasReadAccess, hasNoAccess } =
    featurePermissions[PermissionFeature.DAILY_ACTIVITY];

  const styles = useStyles();
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: useUserSaga });

  const { t } = useTranslation();

  const columns = [
    {
      id: "organizationName",
      label: t("DAILY_ACTIVITY.ORGANIZATIONAL_NAME"),
      sortValue: "organization.name",
      minWidth: 200,
      maxWidth: 300,
      format: (value, index) => {
        return value?.organization?.name;
      },
    },
    {
      id: "activityName",
      label: t("DAILY_ACTIVITY.ACTIVITY_NAME"),
      sortValue: "selector.name",
      minWidth: 280,
      maxWidth: 420,
      format: (value, index) => {
        return value.name;
      },
    },
    {
      id: "shortDescription",
      label: t("DAILY_ACTIVITY.SHORT_DESCRIPTION"),
      sortValue: "selector.shortDescription",
      minWidth: 240,
      maxWidth: 360,
      format: (value: Item) => value.shortDescription || "-",
    },
    {
      id: "description",
      label: t("DAILY_ACTIVITY.DESCRIPTION"),
      sortValue: "selector.description",
      minWidth: 300,
      maxWidth: 460,
    },
    {
      id: "category",
      label: t("DAILY_ACTIVITY.CATEGORY"),
      sortValue: "selector.category",
      minWidth: 180,
      maxWidth: 240,
      format: (value: Item) => value.category || "-",
    },
    {
      id: "uploadedFile",
      label: t("DAILY_ACTIVITY.UPLOADED_FILE_HEADER"),
      format: (val: Item) => <UploadedFileCell resources={val.resources} />,
      minWidth: 230,
      maxWidth: 300,
    },
    {
      id: "time",
      label: t("DAILY_ACTIVITY.TIME"),
      sortValue: "selector.scheduledAt",
      minWidth: 120,
      maxWidth: 180,
      format: (value, index) => {
        return getFormattedDateTime(value?.scheduledAt, " HH:mm");
      },
    },
    {
      id: "sentAmount",
      label: t("DAILY_ACTIVITY.SENT_AMOUNT"),
      sortValue: "selector.sentAmount",
      minWidth: 120,
      maxWidth: 180,
    },
    {
      id: "watchedAmount",
      label: t("DAILY_ACTIVITY.WATCHED_AMOUNT"),
      sortValue: "selector.watchedAmount",
      minWidth: 140,
      maxWidth: 210,
    },
  ];

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(actions.getList());
  }, [q, orderField, orderDirection, organizationId, dispatch]);

  const setPageQuery = (name: string, value: any) => {
    dispatch(actions.setQuery({ name, value }));
  };

  const loading = useSelector(selectLoading);

  const handleEdit = (e) => {
    dispatch(actions.toggleModal({ id: e.id }));
  };

  const handleDuplicate = (e) => {
    dispatch(actions.duplicateDailyActivity({ id: e.id }));
  };

  const hasMoreItems = meta.currentPage < meta.totalPages;
  const getNextPage = () => {
    if (!loading) {
      dispatch(actions.getNextPage(meta.currentPage + 1));
    }
  };

  const handleModal = () => {
    dispatch(actions.toggleModal({ id: 0 }));
  };
  const switchAction = (row, index) => {
    dispatch(actions.toggleStatus(row.id));
  };
  const { height } = useWindowDimensions();
  if (hasNoAccess) {
    return (
      <div className={styles.mainContainer}>
        <CustomDrawer />
        <Topbar />
        <NoPermission />
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <CustomDrawer />
      <Grid item xs={12}>
        <Topbar q={q} setSearch={(q) => setPageQuery("q", q)} />
        <div className={styles.root}>
          <FiltersForm></FiltersForm>
          <>
            <ScrollableView
              loadMore={getNextPage}
              hasMoreItems={hasMoreItems}
              height={height - 280}
            >
              <DataTable
                loading={loading}
                orderDirection={orderDirection}
                orderField={orderField}
                setQuery={setPageQuery}
                rows={items}
                columns={columns}
                Actions={hasFullAccess ? "Edit,Duplicate,Toggle" : ""}
                editAction={handleEdit}
                duplicateAction={handleDuplicate}
                switchAction={switchAction}
              />
            </ScrollableView>
          </>

          <BottomActions
            disabled={!hasFullAccess}
            chipBoxes={[
              {
                label: t("PLACEHOLDERS.TOTAL_ITEMS"),
                count: meta.totalItems,
              },
            ]}
            onClickFab={handleModal}
          />

          <div className={styles.btnDiv}>
            <DailyActivityAddModal />
          </div>
        </div>
      </Grid>
    </div>
  );
};
export default DailyActivity;
